import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';

// Product scoreboard ("double down or kill"), precomputed daily by
// productAnalyticsScoreWorker.js in warehouse-api-lambdas and served as one cached blob by
// the ProductAnalyticsGet lambda. Shapes below mirror the blob's compact keys.

export const SCOREBOARD_URL =
  'https://ck8k2pv7c9.execute-api.ap-southeast-1.amazonaws.com/ProductAPIProduction/product-analytics';

export type ScoreVerdict = 'DOUBLE_DOWN' | 'KEEP' | 'WATCH' | 'KILL' | 'COLLECTING';

// Per stage: [x, n, smoothedRate] — the "all" view additionally has [.., wilsonLo, wilsonHi].
export interface StageRatePack extends Array<number> {}

export interface StratumRates {
  ctr: StageRatePack;
  ss: StageRatePack; // SELECT-SIZE rate (early intent; raw-visit denominator) — scored
  atc: StageRatePack; // ADD-TO-CART rate (conversion; demand-weighted denom) — display-only until mature
  co: StageRatePack;
  pv: StageRatePack;
}

export interface ColorwayBreakdownItem {
  pid: string;
  nm: string;
  vw: number;
  vs: number;
  ss: number; // SELECT-SIZE count
  atc: number; // ADD-TO-CART count
  pu: number;
  ctr: number;
  ssr: number; // SS rate (raw-visit denom)
  atcr: number; // ATC rate (weighted denom)
  pvr: number;
  // Lifetime (all-time) per-colorway figures — greyed subtext in the dropdown. Undefined
  // when the worker didn't attach all-time (e.g. no ALLTIME row for that product yet).
  atVw?: number;
  atVs?: number;
  atPu?: number;
  atCtr?: number;
  atSsr?: number;
  atAtcr?: number;
  atPvr?: number;
}

export interface ProductScoreStyle {
  k: string; // style key
  n: string; // display name (representative colorway)
  live: boolean; // false = all member products deleted from catalog (hidden by default)
  launch: string | null; // earliest member's launch month "YYYY-MM"; null if undated
  pids: string[];
  cw: number; // colorway count
  v: ScoreVerdict;
  s: number; // score (28d)
  s7: number; // score (trailing 7d, same baselines)
  td: number; // s7 - s -> trend
  mat: number; // maturity pips 0-3
  matFrac: number;
  lam: number; // purchase-confidence ramp 0-1
  adj: number; // colorway-count-adjusted composite lift
  fx: { adInf: boolean; addCw: boolean; stok: boolean };
  // Appeal × Intent diagnostic. label ∈ winner|hookNoClose|hiddenGem|weak|highAppeal|
  // highIntent|lowAppeal|lowIntent|average. appeal/intent ∈ hi|avg|lo.
  diag?: { label: string; appeal: string; intent: string };
  yield28?: number; // full-funnel yield 28d: CTR × PV (fraction of card-viewers who buy)
  stk: { oosVw: number; oosVs: number; oosShare: number; availAvg: number };
  eff: { vw: number; vs: number };
  pu28: number;
  puAll: number;
  // Lifetime funnel (all history, combined strata) — shown as greyed subtext under each 28d
  // metric. null when the worker didn't compute it. vw/vs/pu are counts; ctr/atc/co/pv rates.
  allTime: {
    vw: number;
    vs: number;
    pu: number;
    puUnits: number;
    ctr: number;
    ss: number;
    atc: number;
    co: number;
    pv: number;
    yield: number; // full-funnel: all-time CTR × PV (fraction of viewers who buy)
  } | null;
  r: { all: StratumRates; org: StratumRates; ad: StratumRates; unk: StratumRates };
  wk: number[][]; // 8 weekly [atcRate, pvRate] buckets, oldest -> newest
  cws: ColorwayBreakdownItem[];
}

export interface ScoreboardMeta {
  updatedAt: string;
  todayDateKey: string;
  scoredStages?: string[]; // mid-funnel stages currently carrying score weight (e.g. ['ctr','ss','co','pv'])
  kappa: number;
  trendArrowDelta: number;
  baselines: { [stratum: string]: { [stage: string]: number } };
  gates: { views: number; visits: number; killViews: number; killVisits: number };
}

interface ProductScoreboardState {
  styles: ProductScoreStyle[];
  meta: ScoreboardMeta | null;
  dataUpdateTimestamp: string | null;
  getStatus: APIRequestStatus;
}

const initialState: ProductScoreboardState = {
  styles: [],
  meta: null,
  dataUpdateTimestamp: null,
  getStatus: APIRequestStatus.Idle
};

export const fetchProductScoreboard = createAsyncThunk(
  'productAnalyticsList/fetchProductScoreboard',
  async () => {
    const response = await axios({
      method: 'get',
      url: SCOREBOARD_URL
    });
    return response.data;
  }
);

export const productAnalyticsListSlice = createSlice({
  name: 'productAnalyticsList',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchProductScoreboard.pending, (state, action) => {
        state.getStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchProductScoreboard.fulfilled, (state, action) => {
        try {
          const scoreboard =
            typeof action.payload.scoreboard === 'string'
              ? JSON.parse(action.payload.scoreboard)
              : action.payload.scoreboard;
          state.styles = scoreboard?.styles ?? [];
          state.meta = scoreboard?.meta ?? null;
          state.dataUpdateTimestamp = action.payload.dataUpdateTimestamp ?? null;
          state.getStatus = APIRequestStatus.Success;
        } catch (e) {
          console.log('Prodpanel product scoreboard parse failure', e);
          state.getStatus = APIRequestStatus.Failure;
        }
      })
      .addCase(fetchProductScoreboard.rejected, (state, action) => {
        state.getStatus = APIRequestStatus.Failure;
        console.log('Prodpanel product scoreboard fetch is a failure');
      });
  }
});

export default productAnalyticsListSlice.reducer;
